import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FaPlus, FaTrash, FaFilter, FaUndo } from 'react-icons/fa';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { apiService } from '../Config/api.service'; // à adapter selon ton projet
import { OPERATORS_BY_TYPE } from '../Constantes/OperatorByType';


const AdvancedFilter = ({ fields, onApply }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState([{ field: '', operator: '=', value: '' }]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [globalLogic, setGlobalLogic] = useState('AND');

  // Reset all filters
  const resetFilters = () => {
    setFilters([{ field: '', operator: '=', value: '' }]);
    setIncludeArchived(false);
    setGlobalLogic('AND');
  };

  // When changing field, reset operator and value
  const handleFilterChange = (index, key, value) => {
    const updated = [...filters];
    if (key === 'field') {
      updated[index] = { field: value, operator: '=', value: '' };
    } else {
      updated[index][key] = value;
    }
    setFilters(updated);
  };

  const addRule = () => {
    setFilters([...filters, { field: '', operator: '=', value: '' }]);
  };

  const removeRule = (index) => {
    const updated = [...filters];
    updated.splice(index, 1);
    setFilters(updated.length ? updated : [{ field: '', operator: '=', value: '' }]);
  };

  // Async loader for relation fields (many2one/many2many)
  const loadRelationOptions = async (inputValue, relation) => {
    if (!relation) return [];
    try {
      // À adapter selon ton backend, ici on suppose une API générique
      const res = await apiService.getElement(`/relation/${relation}/${inputValue}`);
      return (res.data || []).map(item => ({
        value: item.id,
        label: item.name 
      }));
    } catch {
      return [];
    }
  };

  const handleApply = () => {
  const processedFilters = filters.map(filter => {
    const field = fields.find(f => f.value === filter.field);
    if (field && ['many2one', 'many2many'].includes(field.type)) {
      return {
        ...filter,
        value: filter.value ? filter.value.value : ''
      };
    }
    return filter;
  });
  onApply(processedFilters, includeArchived, globalLogic);
};

  const renderValueInput = (filter, index) => {
    const field = fields.find(f => f.value === filter.field);
    if (!field) {
      return (
        <input
          type="text"
          className="input input-sm input-bordered py-5 w-full"
          value={filter.value}
          onChange={e => handleFilterChange(index, 'value', e.target.value)}
          placeholder={t('advancedFilter.value')}
          disabled
        />
      );
    }

    // Champ de sélection
    if (field.type === 'selection' && Array.isArray(field.selection)) {
      return (
        <Select
          options={field.selection.map(([value, label]) => ({ value, label }))}
          value={
            field.selection.find(([val]) => val === filter.value)
              ? { value: filter.value, label: field.selection.find(([val]) => val === filter.value)[1] }
              : null
          }
          onChange={option => handleFilterChange(index, 'value', option ? option.value : '')}
          placeholder={t('advancedFilter.chooseField', { field: field.string })}
          isClearable
          classNamePrefix="react-select"
        />
      );
    }

    // Champ booléen
    if (field.type === 'boolean') {
      return (
        <select
          className="select select-sm select-bordered w-full"
          value={filter.value}
          onChange={e => handleFilterChange(index, 'value', e.target.value)}
        >
          <option value="">{t('advancedFilter.choose')}</option>
          <option value="true">{t('advancedFilter.yes')}</option>
          <option value="false">{t('advancedFilter.no')}</option>
        </select>
      );
    }

    // Champ relationnel (many2one, many2many)
    if (['many2one', 'many2many'].includes(field.type)) {
      return (
        <AsyncSelect
          cacheOptions
          loadOptions={input => loadRelationOptions(input, field.relation)}
          defaultOptions
          value={filter.value || null}
          onChange={option => handleFilterChange(index, 'value', option)}
          placeholder={t('advancedFilter.searchField', { field: field.string })}
          isClearable
          classNamePrefix="react-select"
        />
      );
    }

    // Champ texte par défaut
    return (
      <input
        type="text"
        className="input input-sm input-bordered py-5 w-full"
        value={filter.value}
        onChange={e => handleFilterChange(index, 'value', e.target.value)}
        placeholder={t('advancedFilter.enterField', { field: field.string })}
      />
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 w-full max-w-3xl mx-auto border border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <FaFilter className="text-[--color-primary]" />
        <h2 className="text-lg font-semibold">{t('advancedFilter.customFilters')}</h2>
        <button
          className="btn btn-xs btn-outline ml-auto"
          onClick={resetFilters}
          title={t('advancedFilter.resetFilters')}
        >
          <FaUndo className="mr-1" /> {t('advancedFilter.reset')}
        </button>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <label className="mr-2 flex gap-2 font-medium items-center">{t('advancedFilter.match')}
          <select
            value={globalLogic}
            onChange={(e) => setGlobalLogic(e.target.value)}
            className="select select-sm select-bordered"
          >
            <option value="AND">{t('advancedFilter.allRules')}</option>
            <option value="OR">{t('advancedFilter.atLeastOneRule')}</option>
          </select>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          {t('advancedFilter.includeArchived')}
        </label>
      </div>

      {filters.map((filter, index) => {
        const field = fields.find(f => f.value === filter.field);
        const operators =
          field && OPERATORS_BY_TYPE[field.type]
            ? OPERATORS_BY_TYPE[field.type]
            : [{ label: '=', value: '=' }, { label: '≠', value: '!=' }];
        return (
          <div key={index} className="flex items-center gap-2 mb-3">
            {/* react-select pour le champ */}
            <div className="w-2/5">
              <Select
                options={fields}
                value={field || null}
                onChange={option => handleFilterChange(index, 'field', option ? option.value : '')}
                placeholder={t('advancedFilter.field')}
                isClearable
                classNamePrefix="react-select"
              />
            </div>

            <select
              className="select select-sm select-bordered w-1/5"
              value={filter.operator}
              onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
              disabled={!field}
            >
              {operators.map((op, idx) => (
                <option key={idx} value={op.value}>{op.label}</option>
              ))}
            </select>

            {/* Champ valeur dynamique */}
            <div className="w-2/5">
              {renderValueInput(filter, index)}
            </div>

            <button
              onClick={() => removeRule(index)}
              className="btn btn-sm btn-ghost"
              title={t('hspForm.delete')}
              tabIndex={0}
              aria-label={t('advancedFilter.deleteRule')}
            >
              <FaTrash />
            </button>
          </div>
        );
      })}

      <div className="flex justify-between items-center mt-4">
        <button onClick={addRule} className="btn btn-sm btn-outline text-[--color-primary]">
          <FaPlus className="mr-1" /> {t('advancedFilter.addRule')}
        </button>

        <button
          onClick={handleApply}
          className="btn btn-sm bg-[--color-primary] text-white"
        >
          {t('advancedFilter.applyFilters')}
        </button>
      </div>
    </div>
  );
};

AdvancedFilter.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      type: PropTypes.string,
      relation: PropTypes.string,
      selection: PropTypes.array,
      string: PropTypes.string,
    })
  ).isRequired,
  onApply: PropTypes.func.isRequired,
};

export default AdvancedFilter;